<template>
  <expandable-drawer
    :class="
      `fixed-bottom expandable-player ${
        playerHidden || (!song && !creating) ? 'hidden' : ''
      } ${createContext || vocalContext ? 'slim-context' : ''}`
    "
    :style="{ 'margin-bottom': bottomTabsVisible ? '64px' : 0 }"
    :change-opacity="false"
    :expanded="forceExpanded"
    :disabled="forceExpanded"
    @expanded="setProgressHidden"
    @collapsed="setProgressVisible"
  >
    <div slot="collapsed" class="position-relative">
      <Button
        v-if="vocalContext && autoVocal && vocalFileId"
        variant="secondary"
        class="mb-2"
        :action="handleDiscard"
        title="Discard audio recording"
        >Discard</Button
      >
      <transition name="fade">
        <div v-if="shouldShowProgress" class="progress-container">
          <div
            v-if="progressVisible"
            class="song-progress"
            :style="{ width: `${songProgress}%` }"
          ></div>
        </div>
      </transition>
      <div class="collapsed d-flex justify-content-between">
        <div v-if="song">
          <div class="song-name name-text">
            {{ song.songName || "New Song" }}
          </div>
          <div class="artist-name name-text">
            {{ song.artistName || "New Artist" }}
          </div>
        </div>
        <button
          v-show="song && initialized"
          type="button"
          class="no-style position-relative"
          v-on:click.stop="handlePlayPause"
        >
          <svg
            class="pause"
            v-if="playing"
            width="12"
            height="16"
            viewBox="0 0 12 16"
            fill="#D5CFF5"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1.5C3 0.671573 2.32843 0 1.5 0C0.671573 0 0 0.671573 0 1.5V14.5C0 15.3284 0.671573 16 1.5 16C2.32843 16 3 15.3284 3 14.5V1.5Z"
            />
            <path
              d="M12 1.5C12 0.671573 11.3284 0 10.5 0C9.67157 0 9 0.671573 9 1.5V14.5C9 15.3284 9.67157 16 10.5 16C11.3284 16 12 15.3284 12 14.5V1.5Z"
            />
          </svg>
          <img
            v-show="!playing && (!willPlay || !loading)"
            src="../assets/svgs/next.svg"
          />
          <div v-show="willPlay && loading" class="loading">
            <lottie-animation
              path="play-pause-light.json"
              :loop="true"
              :autoPlay="false"
              @AnimControl="setAnimController"
            />
          </div>
        </button>
      </div>
    </div>
    <div class="expanded" slot="expanded" @click.stop="noop">
      <div class="song-container">
        <div
          class="waveform"
          v-bind:class="{ hidden: !song || error || loading }"
          v-bind:id="waveform_container_id"
          @click.stop=""
        ></div>
        <div
          v-show="(loading && !error) || (creating && createContext)"
          class="loading-wave"
          id="new_song_loading"
        >
          <lottie-animation
            path="remix-just-waves.json"
            :loop="true"
            :autoPlay="false"
            @AnimControl="setWaveAnimController"
          />
        </div>
        <div v-if="error" class="error-text pt-4 pb-4" id="new_song_error">
          <div id="error_message" class="text-danger">
            Network error. Check your connection and reload.
            <div class="mt-3">
              <Button v-on:click.prevent="retryLoad" variant="secondary">
                reload
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="includeActions"
        class="button-container d-flex flex-row justify-content-between pb-2"
      >
        <template v-if="createContext">
          <Button
            v-if="song"
            variant="secondary"
            class="flex-grow-1 mr-2"
            :action="playNextOrCreate"
            >{{ song.saved ? "Next" : "Reject" }}</Button
          >
          <Button
            v-if="song"
            class="flex-grow-1"
            :action="saveSong"
            :disabled="isSaving || song.saved"
            >{{ song.saved ? "Saved" : "Save" }}</Button
          >
        </template>

        <template v-if="vocalContext">
          <Button
            v-if="autoVocal && vocalFileId"
            variant="secondary"
            class="flex-grow-1 mr-2"
            :action="createAutoVocal"
            >Reject auto-vocal</Button
          >
          <Button
            v-else
            variant="secondary"
            class="flex-grow-1 mr-2"
            :action="handleDiscard"
            title="Discard audio recording"
            >Discard</Button
          >
          <Button v-if="song" class="flex-grow-1" :action="handleGoToCreator"
            >Save</Button
          >
        </template>
        <template v-if="!createContext && !vocalContext">
          <Button
            variant="primary-player"
            class="tags-container button-slot flex-grow-1 mr-2"
          >
            <span v-for="tag in song.tags" :key="tag" class="mr-1">
              <span @click="navToTag(tag)" class="song-link">#{{ tag }}</span>
            </span>
          </Button>
          <Button
            class="button-slot"
            v-if="!song.streamUrl"
            :action="handleShare"
            >Share</Button
          >
          <Button v-else class="button-slot" :action="handleStream"
            >Stream</Button
          >
        </template>
      </div>
    </div>
  </expandable-drawer>
</template>

<script>
import ExpandableDrawer from "@/components/ExpandableDrawer";
import WaveformPlayer from "@/components/WaveformPlayer";
import Button from "@/components/Button";
import Auth from "@/services/Auth";
import { noop } from "@/utils";
import { PLAYER_CONTEXTS } from "@/constants";
import { ROUTES } from "@/router/routes";
import SongService from "@/services/Song";

export default {
  name: "ExpandablePlayer",
  extends: WaveformPlayer,
  components: {
    ExpandableDrawer,
    Button
  },
  props: {
    forceOpen: {
      type: Boolean,
      default: false
    },
    includeActions: {
      type: Boolean,
      default: true
    },
    globalPlayer: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      progressVisible: true,
      isSaving: false,
      windowWidth: window.innerWidth,
      smallLoader: null,
      waveAnimation: null,
      willPlay: true
    };
  },
  watch: {
    smallLoader(value) {
      if (this.loading && value) {
        this.smallLoader.playSegments(
          [
            [0, 81],
            [34, 76]
          ],
          true
        );
      }
    },
    waveAnimation(value) {
      if ((this.loading || this.creating) && value) {
        this.playWaveAnimation();
      }
    },
    creating(newValue, oldValue) {
      if (newValue && newValue !== oldValue && this.waveAnimation) {
        this.playWaveAnimation();
      }
    },
    loading(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        if (this.smallLoader) {
          this.smallLoader.playSegments(
            [
              [0, 81],
              [34, 76]
            ],
            true
          );
        }
        if (this.waveAnimation) {
          this.playWaveAnimation();
        }
      } else if (!newValue && newValue !== oldValue) {
        if (this.shouldPlay) {
          if (this.smallLoader) {
            this.smallLoader.stop();
          }
          if (this.waveAnimation) {
            this.waveAnimation.stop();
          }
          this.playSong();
        }
      }
    },
    playerContext(context) {
      if (
        context === PLAYER_CONTEXTS.create ||
        context === PLAYER_CONTEXTS.vocal
      ) {
        this.$forceUpdate();
      }
    },
    playerHidden(value) {
      if (value) {
        this.wavesurfer.pause();
      }
    },
    forceExpanded(value) {
      if (value) {
        this.setProgressHidden();
      }
    }
  },
  computed: {
    shouldPlay() {
      return this.willPlay && !this.songContext;
    },
    loggedInUserId() {
      return this.$store.getters["user/loggedInUserId"];
    },
    songProgress() {
      const progress = (this.elapsed / this.duration) * 100;
      return progress > 0 ? progress : 0;
    },
    smallScreen() {
      return this.windowWidth < 1024;
    },
    navVisible() {
      return this.$store.getters["app/getShowHeader"];
    },
    bottomTabsVisible() {
      return this.smallScreen && this.navVisible;
    },
    songContext() {
      return this.playerContext === PLAYER_CONTEXTS.song;
    },
    createContext() {
      return this.playerContext === PLAYER_CONTEXTS.create;
    },
    vocalContext() {
      return this.playerContext === PLAYER_CONTEXTS.vocal;
    },
    playerContext() {
      return this.$store.getters["player/getContext"];
    },
    playerHidden() {
      return this.playerContext === PLAYER_CONTEXTS.hidden;
    },
    creating() {
      return this.$store.getters["creator/getCreatingMore"];
    },
    shouldShowProgress() {
      return this.progressVisible && !this.forceExpanded;
    },
    forceExpanded() {
      return this.createContext || this.songContext || this.vocalContext;
    },
    autoVocal() {
      return this.$store.getters["creator/getAutoVocal"];
    },
    vocalFileId() {
      return this.$store.getters["creator/getVocalFileId"];
    }
  },
  methods: {
    navToTag(tag) {
      this.$router.push({
        path: `/tag/${tag}`
      });
      this.$forceUpdate();
    },
    setProgressVisible() {
      this.progressVisible = true;
    },
    setProgressHidden() {
      this.progressVisible = false;
    },
    setAnimController(instance) {
      this.smallLoader = instance;
    },
    setWaveAnimController(instance) {
      this.waveAnimation = instance;
    },
    playWaveAnimation() {
      this.waveAnimation.playSegments(
        [
          [0, 124],
          [92, 124]
        ],
        true
      );
    },
    handleDiscard() {
      this.$store.dispatch("song/clearMergeTrack", this.activeSongId);
      this.$store.commit("player/SET_ACTIVE", null);
      this.$amplitude.getInstance().logEvent("create_discard_vocal", {
        song_id: this.activeSongId
      });
    },
    createAutoVocal() {
      this.$store.dispatch("creator/createVocal");
      this.$store.commit("player/SET_ACTIVE", null);
      this.$amplitude.getInstance().logEvent("create_autovocal", {
        song_id: this.activeSongId
      });
    },
    handleGoToCreator() {
      this.$router.push({
        path: ROUTES.createWithId.path.replace(":id", this.activeSongId)
      });
    },
    handleShare() {
      this.$amplitude.getInstance().logEvent("song_share", {
        song_id: this.activeSongId
      });
      this.recaptchaAction("song_share");
      const route = this.$router.resolve({
        path: ROUTES.s.path.replace(":id", this.activeSongId)
      });
      window.open(route.href, "_blank");
    },
    handleStream() {
      this.$amplitude.getInstance().logEvent("song_stream", {
        song_id: this.activeSongId
      });
      this.recaptchaAction("song_stream");
      if (this.song && this.song.streamUrl) {
        window.open(this.song.streamUrl, "_blank", "noopener");
      }
    },
    handlePlayPause() {
      if (!this.loading) {
        this.willPlay = !this.playing;
        this.playSong();
      } else {
        this.willPlay = true;
      }
    },
    noop: noop,
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    async recaptchaAction(action) {
      await this.$recaptchaLoaded();
      await this.$recaptcha(action);
    },
    async playNextOrCreate() {
      if (this.createContext) {
        const action =
          this.song && this.song.saved ? "create_next" : "create_reject";
        await this.recaptchaAction(action);
        await this.$store.dispatch("creator/playNextOrCreate", this.$amplitude);
      }
    },
    async saveSong() {
      const songId = this.activeSongId;
      this.$amplitude.getInstance().logEvent("create_save", {
        song_id: songId
      });
      this.isSaving = true;
      this.$store.dispatch("song/saveSong", songId).then(
        result => {
          this.isSaving = false;
        },
        error => {
          console.log(error);
          this.isSaving = false;
        }
      );
      await this.recaptchaAction("save_song");
    }
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize);
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  }
};
</script>

<style scoped lang="scss">
.song-link {
  color: rgba(209, 194, 255, 0.7);
}
.song-link:hover {
  color: rgba(209, 194, 255, 1);
}
.expandable-player {
  width: 640px;
  margin-left: auto;
  margin-right: auto;
  z-index: 998;
  @media (max-width: 1023px) {
    width: calc(100% - 16px);
    margin-left: 8px;
    margin-right: 8px;
  }
  &.slim-context {
    @media (max-width: 575px) {
      width: calc(100% - 16px);
      margin-left: 8px;
      margin-right: 8px;
    }
    width: 468px;
    margin-left: auto;
    margin-right: auto;
  }
}
.progress-container {
  position: absolute;
  height: 6px;
  width: 100%;
  background-color: #362468;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  clip-path: inset(0 0 2px);
  overflow: hidden;
}
.song-progress {
  height: 6px;
  background-color: #21ecd8;
}
.collapsed {
  background-color: #4d2f8d;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  padding: 16px 0 16px 16px;
}
.expanded {
  background-color: #09090e;
}
.waveform {
  height: 74px;
  &.hidden {
    opacity: 0;
    margin-top: -74px;
  }
}
.song-container {
  background-color: #4d2f8d;
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
  padding: 10px 14px 14px;
  margin-bottom: 8px;
  position: relative;
}
.button-container {
}
.song-name {
  color: #f2eeff;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.02em;
}
.name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1; /* number of lines to show */
  -webkit-box-orient: vertical;
}
.loading {
  position: absolute;
  top: 8px;
  right: 12px;
  width: 26px;
  height: 26px;
}
.loading-wave {
  height: 74px;
}
.pause {
  margin-right: 3px;
}
.artist-name {
  @extend .song-name;
  font-weight: 300;
}
.no-style {
  display: inline-block;
  border: none;
  padding: 8px 16px;
  margin: 0;
  text-decoration: none;
  background: transparent;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  &:focus {
    outline: none;
  }
}
.button-slot {
  min-width: 109px;
}
.tags-container {
  background-color: #362468;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 22px;
  letter-spacing: 0.04em;
  color: rgba(209, 194, 255, 0.7);
  white-space: nowrap;
  overflow-y: hidden;
  overflow-x: scroll;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

::-webkit-scrollbar {
  height: 8px;
  width: 8px;
  background: transparent;
}
</style>
